import React, { useEffect } from "react";
import { HashRouter as Router, Routes, Route } from "react-router-dom";
import axios from "axios";
import { onIdTokenChanged } from "firebase/auth";
import MainLayout from "./layouts/MainLayout";
import AuthLayout from "./layouts/AuthLayout";
import Home from "./pages/Home";
import TalentPool from "./pages/TalentPool";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import ThemeProvider from "./theme";
import { Provider, useDispatch } from "react-redux";
import store, { AppDispatch } from "./store";
import DepartmentsPage from "./pages/Departments";
import NotFoundPage from "./pages/NotFound";
import PositionsPage from "./pages/Positions";
import LoginPage from "./pages/Login";
import SignUpPage from "./pages/SignUp";
import ProtectedRoute from "./components/ProtectedRoute";
import ForgotPasswordPage from "./pages/ForgotPassword";
import EmailVerificationPage from "./pages/EmailVerification";
import { hydrateUser, logoutUser, syncToken } from "./features/authSlice";
import ProfilePage from "./pages/Profile";
import JobDetailPage from "./components/position/JobDetailPage";
import { SnackbarProvider } from "./components/SnackbarProvider";
import SettingsPage from "./pages/Settings";
import SurveyPage from "./pages/SurveyPage";
import VisitorLayout from "./layouts/VisitorLayout";
import { auth } from "./firebase";
import { API_URL } from "./config/api";

const AppWithStore = () => {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    dispatch(hydrateUser());
  }, [dispatch]);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (!user) {
        return;
      }

      try {
        const freshToken = await user.getIdToken();
        if (localStorage.getItem("token") !== freshToken) {
          dispatch(syncToken(freshToken));
        }
      } catch (error) {
        console.error("Failed to sync refreshed Firebase token:", error);
      }
    });

    return unsubscribe;
  }, [dispatch]);

  useEffect(() => {
    const isBackendRequest = (url?: string) => {
      if (!url) {
        return false;
      }

      return url.startsWith(API_URL);
    };

    const requestInterceptorId = axios.interceptors.request.use(
      async (config) => {
        const hasStoredSession = Boolean(localStorage.getItem("user"));
        const hasAuthorizationHeader = Boolean((config.headers as any)?.Authorization);

        if (!isBackendRequest(config.url) || (!hasStoredSession && !hasAuthorizationHeader)) {
          return config;
        }

        const currentUser = auth.currentUser;
        if (!currentUser) {
          return config;
        }

        const freshToken = await currentUser.getIdToken();
        if (localStorage.getItem("token") !== freshToken) {
          dispatch(syncToken(freshToken));
        }

        const headers = (config.headers ?? {}) as Record<string, string>;
        headers.Authorization = `Bearer ${freshToken}`;
        config.headers = headers as any;

        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error?.config as any;

        if (
          error?.response?.status === 401 &&
          isBackendRequest(originalRequest?.url) &&
          !originalRequest?._retry
        ) {
          const currentUser = auth.currentUser;
          if (currentUser) {
            try {
              originalRequest._retry = true;

              const freshToken = await currentUser.getIdToken(true);
              dispatch(syncToken(freshToken));

              const headers = (originalRequest.headers ?? {}) as Record<string, string>;
              headers.Authorization = `Bearer ${freshToken}`;
              originalRequest.headers = headers;

              return axios(originalRequest);
            } catch (refreshError) {
              console.error("Failed to refresh Firebase token after 401:", refreshError);
            }
          }

          void dispatch(logoutUser());
          if (window.location.hash !== "#/login") {
            window.location.hash = "#/login";
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptorId);
      axios.interceptors.response.eject(responseInterceptorId);
    };
  }, [dispatch]);

  return (
    <Router>
      <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Home />} />
          <Route path="candidates" element={<TalentPool />} />
          <Route path="department" element={<DepartmentsPage />} />
          <Route path="positions" element={<PositionsPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="positions/:id" element={<JobDetailPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>
        <Route path="/" element={<AuthLayout />}>
          <Route path="*" element={<NotFoundPage />} />
          <Route path="login" element={<LoginPage />} />
          <Route path="signup" element={<SignUpPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="verify-email" element={<EmailVerificationPage />} />
        </Route>
        <Route path="/" element={<VisitorLayout />}>
          <Route path="survey" element={<SurveyPage />} />
        </Route>
      </Routes>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <SnackbarProvider>
      <Provider store={store}>
        <ThemeProvider>
          <I18nextProvider i18n={i18n}>
            <AppWithStore />
          </I18nextProvider>
        </ThemeProvider>
      </Provider>
    </SnackbarProvider>
  );
};

export default App;
